// lib/list/listOutputGroups.ts

import { extractBlobPathFromInternalUrl, storageAccountName, userFilesContainerName } from '#lib/azure-shared';

type ListGroupableOutput = {
  id: string;
  ts: number;
  batchKey: string;
  slotKey?: string;
};

type ListOutputGroup<T> = {
  key: string;
  label: string;
  outputs: T[];
};

type ListOutputIdentity = {
  id: string;
  assetUrl?: string | null;
};

export const getListAssetFileKey = (value: string | null | undefined): string => {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';
  const path = raw.split(/[?#]/)[0] ?? '';
  try {
    const internalPath = extractBlobPathFromInternalUrl(path);
    if (internalPath) return `user-file:${internalPath}`;
    const url = new URL(raw);
    const containerPrefix = `/${userFilesContainerName}/`;
    if (url.origin === `https://${storageAccountName}.blob.core.windows.net`
      && url.pathname.startsWith(containerPrefix)) {
      return `user-file:${decodeURIComponent(url.pathname.slice(containerPrefix.length))}`;
    }
  } catch {
    // Invalid/legacy URLs still have an identity, but must not alias a different object.
  }
  return raw;
};

export const sameListAssetFile = (left: string, right: string): boolean => {
  const key = getListAssetFileKey(left);
  return Boolean(key) && key === getListAssetFileKey(right);
};

export const dedupeListOutputs = <T extends ListOutputIdentity>(outputs: T[]): T[] => {
  const ids = new Set<string>();
  const assetUrls = new Set<string>();
  return outputs.filter((output) => {
    const assetUrl = getListAssetFileKey(output.assetUrl);
    if (ids.has(output.id) || (assetUrl && assetUrls.has(assetUrl))) return false;
    ids.add(output.id);
    if (assetUrl) assetUrls.add(assetUrl);
    return true;
  });
};

const compareOutputs = <T extends ListGroupableOutput>(left: T, right: T) =>
  right.ts - left.ts || (left.slotKey ?? left.id).localeCompare(right.slotKey ?? right.id);

const formatDateGroup = (timestamp: number): string => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 'Earlier';
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const buildListOutputGroups = <T extends ListGroupableOutput>(
  outputs: T[],
  mode: 'date' | 'run',
): ListOutputGroup<T>[] => {
  if (outputs.length === 0) return [];

  if (mode === 'run') {
    const byBatch = new Map<string, T[]>();
    for (const output of outputs) {
      const key = output.batchKey.trim() || output.id;
      const members = byBatch.get(key);
      if (members) members.push(output);
      else byBatch.set(key, [output]);
    }
    const batches = [...byBatch.entries()].map(([key, members]) => ({
      key,
      outputs: [...members].sort(compareOutputs),
      ts: Math.max(...members.map((output) => Number.isFinite(output.ts) ? output.ts : 0)),
    })).sort((left, right) => right.ts - left.ts || left.key.localeCompare(right.key));
    const runNumberByKey = new Map(
      [...batches].reverse().map((batch, index) => [batch.key, index + 1]),
    );
    return batches.map((batch) => ({
      key: batch.key,
      label: `Run ${runNumberByKey.get(batch.key)}`,
      outputs: batch.outputs,
    }));
  }

  const byDay = new Map<string, { label: string; ts: number; outputs: T[] }>();
  for (const output of outputs) {
    const label = formatDateGroup(output.ts);
    const existing = byDay.get(label);
    if (existing) {
      existing.outputs.push(output);
      existing.ts = Math.max(existing.ts, Number.isFinite(output.ts) ? output.ts : 0);
    } else {
      byDay.set(label, {
        label,
        ts: Number.isFinite(output.ts) ? output.ts : 0,
        outputs: [output],
      });
    }
  }
  return [...byDay.values()]
    .sort((left, right) => right.ts - left.ts || left.label.localeCompare(right.label))
    .map((group) => ({
      key: group.label,
      label: group.label,
      outputs: group.outputs.sort(compareOutputs),
    }));
};
